import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, Crown, Lock, Smartphone, ArrowRight, Download, ShieldCheck, Apple, AlertTriangle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { isFirebaseConfigured } from "@/lib/firebase";
import { listFirebaseApps } from "@/lib/firebaseApps";
import { getCatalogApp, isAppAvailable } from "@/lib/catalog";
import { toast } from "sonner";

const GITHUB_RELEASE_ANDROID_FREE_URL = "https://github.com/NyiNyiSein/MyanTax_Lite/releases/download/v1.0.0/myantaxboxlite.apk";

const EDITIONS = {
  android: {
    appId: "local_android_tax_calculator",
    label: "Android Edition",
    sub: "Android ဖုန်း / Tablet အတွက်",
    icon: Smartphone,
    freeLabel: "Free Android APK",
    fileName: "MyanTaxBox-Free.apk",
    downloadUrl: GITHUB_RELEASE_ANDROID_FREE_URL,
  },
  ios: {
    appId: "local_ios_tax_calculator",
    label: "iOS Edition",
    sub: "iPhone / iPad အတွက်",
    icon: Apple,
    freeLabel: "Free iOS Preview",
    fileName: "MyanTaxBox-Free.ipa",
  },
};

const FREE_FEATURES = [
  "Salary calculator",
  "အခွန်ဥပဒေများ အခြေခံကြည့်ရှုနိုင်",
  "Tax calendar",
  "FAQ",
];

const PREMIUM_FEATURES = [
  "အခွန်ဥပဒေ၊ နှုန်းထား၊ ဇယားများ အပြည့်အစုံ",
  "CBM ငွေလဲနှုန်း converter နှင့် cached rates",
  "အရေးကြီးရက်စွဲများ၊ ဒဏ်ကြေးများနှင့် သတိပေးချက်များ",
  "အခွန်ပုံစံများ ကြည့်ရှု/ဖြည့်သွင်းနိုင်",
  "Salary, Corporation, Commercial, WHT စသည့် အခွန်တွက်စက်များ",
  "PDF report ထုတ်ယူ/ပုံနှိပ်နိုင်",
];

export default function Trial() {
  const { edition = "android" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const selected = EDITIONS[edition] || EDITIONS.android;
  const Icon = selected.icon;
  const [downloadApp, setDownloadApp] = useState(null);

  // If the requested edition exists in the catalog but isn't released yet,
  // show a "coming soon" view rather than letting the user attempt downloads/purchase.
  const catalogApp = getCatalogApp(selected.appId);
  const available = !catalogApp || isAppAvailable(catalogApp);

  useEffect(() => {
    let cancelled = false;

    async function loadDownloadApp() {
      if (!isFirebaseConfigured) return;
      try {
        const apps = await listFirebaseApps();
        const selectedPlatform = selected.appId.includes("ios") ? "ios" : "android";
        const platformApps = apps.filter((app) => app.platform === selectedPlatform);
        const freeApp = platformApps.find((app) => /free|lite/i.test(app.title || "")) ||
          platformApps.find((app) => Number(app.price_mmk || 0) === 0);
        if (!cancelled) setDownloadApp(freeApp || null);
      } catch {
        if (!cancelled) setDownloadApp(null);
      }
    }

    loadDownloadApp();
    return () => { cancelled = true; };
  }, [selected.appId]);

  const goPremium = () => {
    if (!user) {
      toast.info("Premium ဝယ်ရန် အရင် login/register လုပ်ပါ");
      navigate("/login", { state: { from: `/checkout/${selected.appId}` } });
      return;
    }
    navigate(`/checkout/${selected.appId}`);
  };

  const downloadFree = () => {
    // Android free version downloads from GitHub Releases
    if (selected.downloadUrl) {
      window.open(selected.downloadUrl, "_blank");
      return;
    }
    // Fallback: iOS still uses Firebase
    if (downloadApp?.file_url) {
      window.location.href = downloadApp.file_url;
      return;
    }
    toast.error(`${selected.fileName} file မတွေ့သေးပါ။ Admin panel မှ Free/Lite app file ကို upload လုပ်ပါ။`);
  };

  if (!available) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-sm text-zinc-500 hover:text-[#0F4E2A]">← ပင်မစာမျက်နှာသို့</Link>

        <div className="mt-6 bg-white border border-zinc-200 rounded-3xl p-8 sm:p-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-zinc-100 grid place-items-center text-zinc-400 mx-auto">
            <Icon className="w-8 h-8" />
          </div>
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-zinc-100 px-2.5 py-1 text-xs font-semibold text-zinc-600">
            <Clock className="w-3.5 h-3.5" /> မကြာမီ ရနိုင်ပါမည်
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">{selected.label}</h1>
          <p className="mt-3 text-zinc-600 leading-7 max-w-xl mx-auto">
            iOS ဗားရှင်းကို ကြိုတင်ပြင်ဆင်နေပါသည်။ အခုအခါတွင် Android ဗားရှင်းကို ရွေးချယ်အသုံးပြုနိုင်ပါသည်။
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild className="h-12 bg-[#7CC576] hover:bg-[#5FB259] rounded-lg">
              <Link to="/try/android"><Smartphone className="w-4 h-4 mr-2" /> Android ဗားရှင်း စမ်းရန်</Link>
            </Button>
            <Button asChild variant="outline" className="h-12 rounded-lg border-[#0F4E2A]/30 text-[#0F4E2A] hover:bg-[#E8F5E5]">
              <Link to="/">ပင်မစာမျက်နှာ</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <Link to="/" className="text-sm text-zinc-500 hover:text-[#0F4E2A]">← ပင်မစာမျက်နှာသို့</Link>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <section className="lg:col-span-7 bg-white border border-zinc-200 rounded-3xl p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#E8F5E5] grid place-items-center text-[#0F4E2A]">
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <div className="text-sm tracking-wide text-zinc-500 font-medium">Free App Version</div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{selected.label}</h1>
              <p className="text-sm text-zinc-600 mt-1">{selected.sub}</p>
            </div>
          </div>

          <div className="mt-8 rounded-2xl border border-zinc-200 bg-zinc-50 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="w-20 h-20 rounded-2xl bg-white border border-zinc-200 grid place-items-center text-[#0F4E2A] flex-shrink-0">
                <Download className="w-9 h-9" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-medium">{selected.freeLabel}</div>
                <h2 className="text-2xl font-bold mt-1">Free version ကို အရင် download စမ်းသုံးပါ</h2>
                <p className="text-sm text-zinc-600 mt-2">
                  Premium မဝယ်ခင် MyanTax Box free version ကို install လုပ်ပြီး UI နှင့် အခြေခံ feature များကို စမ်းသုံးနိုင်ပါတယ်။
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button onClick={downloadFree} className="h-12 bg-[#7CC576] hover:bg-[#5FB259] rounded-lg">
                <Download className="w-4 h-4 mr-2" /> Free Version Download
              </Button>
              <Button onClick={goPremium} variant="outline" className="h-12 rounded-lg border-[#0F4E2A]/30 text-[#0F4E2A] hover:bg-[#E8F5E5]">
                Premium ဝယ်ယူရန် <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <div className="mt-5 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Free version တွင် feature limit ရှိပြီး premium status ကို admin approve ပြီးနောက် full features unlock ဖြစ်ပါမည်။</span>
            </div>

            {edition === "android" && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Note: Users will need to enable "Install from unknown sources" on their Android device to sideload the APK.</span>
              </div>
            )}
          </div>
        </section>

        <aside className="lg:col-span-5 space-y-5">
          <FeaturePanel title="Free features" icon={Check} items={FREE_FEATURES} />
          <FeaturePanel title="Premium unlocks" icon={Crown} items={PREMIUM_FEATURES} premium />
          <div className="bg-white border border-zinc-200 rounded-2xl p-5">
            <Button onClick={goPremium} className="w-full h-12 bg-[#0F4E2A] hover:bg-[#0A3A1F] rounded-lg">
              Premium ဝယ်ယူရန် <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-zinc-500 text-center mt-3">ငွေပေးချေမှု စစ်ဆေးပြီးနောက် Premium အသုံးပြုခွင့် ဖွင့်ပေးပါမည်။</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function FeaturePanel({ title, icon: Icon, items, premium }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5">
      <div className="flex items-center gap-2 font-semibold">
        <Icon className={`w-5 h-5 ${premium ? "text-amber-500" : "text-emerald-600"}`} />
        {title}
      </div>
      <ul className="mt-4 space-y-2.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-zinc-700">
            {premium ? <Lock className="w-4 h-4 mt-0.5 text-zinc-400" /> : <Check className="w-4 h-4 mt-0.5 text-emerald-600" />}
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
